import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useMotionTemplate } from 'framer-motion';
import { useConfig } from '../../contexts/ConfigContext';
import {
  ArrowRight, PlayCircle, ShieldCheck, AlertTriangle,
  CircleDot, FileText, BookOpenCheck, Sparkles, ChevronRight,
} from 'lucide-react';
import { SplitText } from './Reveal';

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { config } = useConfig();
  const allowSelfSignup = config?.enable_self_signup === true;

  // Mouse-tracked subtle cyan glow
  const mx = useMotionValue(50);
  const my = useMotionValue(20);
  const glow = useMotionTemplate`radial-gradient(700px circle at ${mx}% ${my}%, rgba(56,189,248,0.14), transparent 60%)`;
  const containerRef = useRef(null);
  const onMouseMove = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 100);
    my.set(((e.clientY - r.top) / r.height) * 100);
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={onMouseMove}
      className="relative overflow-hidden hero-grid-bg"
    >
      {/* Atmospheric layers */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1759735541630-036eefb7cd3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNoJTIwYmFja2dyb3VuZCUyMGN5YW58ZW58MHx8fHwxNzc4MDgyMTgyfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#071120]/55 via-[#071120]/85 to-[#071120]" />
      </div>
      <motion.div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: glow }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left column — copy */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 mb-7 backdrop-blur-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-glow animate-pulse" />
              <span className="font-mono tracking-wide">Private Pilot Program · Invite-only</span>
            </motion.div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] xl:text-6xl font-bold text-white leading-[1.04] tracking-tight">
              <SplitText text="Validate shipment documents" baseDelay={0.1} charDelay={0.02} className="block" />
              <span className="text-cyan-glow text-glow block mt-1.5">
                <SplitText text="before customs review" baseDelay={0.75} charDelay={0.022} />
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.45, ease: EASE }}
              className="mt-7 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed"
            >
              ComplyTradeIQ helps importers, exporters, and compliance teams detect missing documents,
              cross-document mismatches, and shipment risks before submission.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.6, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            >
              {allowSelfSignup ? (
                <Link
                  to="/signup"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold transition-all glow-cyan hover:bg-cyan-hover hover:scale-[1.02] active:scale-[0.99]"
                  data-testid="hero-primary-cta"
                >
                  Start Validation
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              ) : (
                <Link
                  to="/request-access"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold transition-all glow-cyan hover:bg-cyan-hover hover:scale-[1.02] active:scale-[0.99]"
                  data-testid="hero-primary-cta"
                >
                  Request Pilot Access
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
              <a
                href="#workflow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white hover:bg-white/5 hover:border-white/25 transition-colors"
                data-testid="hero-secondary-cta"
              >
                <PlayCircle className="h-4 w-4" />
                View Demo Workflow
              </a>
            </motion.div>

            {/* Operational trust indicators */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.75, ease: EASE }}
              className="mt-10 grid grid-cols-3 gap-x-4 max-w-md"
            >
              <TrustStat value="6" label="Trade documents reconciled" />
              <TrustStat value="< 90s" label="Pre-shipment review" />
              <TrustStat value="0" label="Auto-submissions" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.9 }}
              className="mt-8 text-[11px] text-slate-500 font-mono tracking-wider uppercase"
            >
              AI interprets · Rules decide · Humans act
            </motion.div>
          </div>

          {/* Right column — operational control-tower mockup */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.95, delay: 1.2, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-6 bg-gradient-to-tr from-cyan-glow/15 via-transparent to-cyan-glow/10 blur-3xl rounded-3xl pointer-events-none" />
              <ControlTowerMockup />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStat({ value, label }) {
  return (
    <div className="border-l border-white/10 pl-3 first:border-l-0 first:pl-0">
      <div className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

function ControlTowerMockup() {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-navy-900/80 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-navy-900/60">
        <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-500/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
        <span className="ml-3 text-[11px] text-slate-500 font-mono">app.complytradeiq.com / shipments / CTQ-2026-0041</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-emerald-400 font-mono">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          live
        </span>
      </div>

      <div className="p-5 space-y-4 bg-[#071120]/40">
        {/* Shipment card */}
        <div className="rounded-xl border border-white/10 bg-navy-900/80 p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Shipment · Sea Freight</div>
              <div className="font-display text-base font-semibold text-white mt-1 truncate">Organic Moringa Powder</div>
              <div className="text-xs text-slate-400 mt-0.5 font-mono flex items-center gap-1.5">
                <span>India</span>
                <ChevronRight className="h-3 w-3 text-slate-600" />
                <span>Finland</span>
                <span className="text-slate-700">·</span>
                <span>1000 kg</span>
                <span className="text-slate-700">·</span>
                <span>HS 1211.90</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px] font-medium shrink-0">
              <AlertTriangle className="h-3 w-3" />
              Validation warning
            </div>
          </div>

          {/* Checklist progress */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Document checklist</span>
              <span className="text-xs font-mono text-slate-300"><span className="text-cyan-glow">4</span> / 6</span>
            </div>
            <div className="h-1.5 rounded-full bg-navy-800 overflow-hidden">
              <div className="h-full w-[66%] bg-gradient-to-r from-cyan-glow to-cyan-hover" />
            </div>
          </div>

          {/* Document chips */}
          <div className="flex flex-wrap gap-1.5">
            <DocChip label="Commercial Invoice" status="pass" />
            <DocChip label="Packing List" status="warning" />
            <DocChip label="Bill of Lading" status="pass" />
            <DocChip label="Cert. of Origin" status="missing" />
            <DocChip label="Cert. of Analysis" status="pending" />
            <DocChip label="Organic Cert." status="pending" />
          </div>
        </div>

        {/* Issues panel */}
        <div className="rounded-xl border border-white/10 bg-navy-900/80 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Cross-document validation</span>
            <span className="text-[10px] text-slate-400 font-mono">3 findings</span>
          </div>
          <ul className="space-y-2">
            <IssueRow severity="blocking" title="Missing Certificate of Origin" detail="Required for EU import · authorised chamber" />
            <IssueRow severity="warning" title="Quantity mismatch" detail="Commercial Invoice 1000 kg ↔ Packing List 980 kg" />
            <IssueRow severity="warning" title="Organic Certificate recommended" detail="EU 2018/848 — labelling compliance" />
          </ul>
        </div>

        {/* Footer row: Source-backed guidance + Report ready */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-white/10 bg-navy-900/80 p-3.5">
            <div className="flex items-center gap-1.5 mb-2">
              <BookOpenCheck className="h-3.5 w-3.5 text-cyan-glow" />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Source-backed guidance</span>
            </div>
            <div className="text-[11px] text-slate-300 leading-relaxed">EU Reg. <span className="font-mono text-cyan-glow">2454/93 Art. 47</span></div>
            <div className="text-[10px] text-slate-500 mt-0.5">Non-preferential CO requirement</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-navy-900/80 p-3.5 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-cyan-glow/10 border border-cyan-glow/20 grid place-items-center text-cyan-glow shrink-0">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-white font-medium leading-tight">Report ready for review</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Human review recommended</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocChip({ label, status }) {
  const styles = {
    pass: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    warning: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    missing: 'bg-red-500/10 border-red-500/20 text-red-400',
    pending: 'bg-slate-500/10 border-slate-500/20 text-slate-400',
  }[status];
  const dot = {
    pass: 'bg-emerald-400',
    warning: 'bg-amber-400',
    missing: 'bg-red-400',
    pending: 'bg-slate-500',
  }[status];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-md border ${styles}`}>
      <span className={`h-1 w-1 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function IssueRow({ severity, title, detail }) {
  const icon = severity === 'blocking'
    ? { color: 'bg-red-500/10 border-red-500/20 text-red-400', Icon: ShieldCheck }
    : { color: 'bg-amber-500/10 border-amber-500/20 text-amber-400', Icon: AlertTriangle };
  return (
    <li className="flex items-start gap-2.5">
      <div className={`h-6 w-6 rounded-md grid place-items-center border shrink-0 ${icon.color}`}>
        <icon.Icon className="h-3 w-3" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-white font-medium truncate">{title}</div>
        <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{detail}</div>
      </div>
      <CircleDot className="h-3 w-3 text-slate-600 shrink-0 mt-1.5" />
    </li>
  );
}
